/**
 * ManagerPerformanceTable Component
 * Table showing all managers' team performance
 */

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { Card } from '../../common';
import {
  User,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronUp,
  ChevronDown,
  Search,
  Users,
} from 'lucide-react';
import type { ManagerPerformanceSummary } from '../../../types';
import { getScoreColor, getScoreCategory } from '../../../types';

export interface ManagerPerformanceTableProps {
  managers: ManagerPerformanceSummary[];
  title?: string;
  onManagerClick?: (managerId: string) => void;
  className?: string;
  isLoading?: boolean;
}

type SortField = 'name' | 'score' | 'teamSize';
type SortDirection = 'asc' | 'desc';

const CATEGORY_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  needsImprovement: 'Needs Improvement',
  poor: 'Poor',
};

export const ManagerPerformanceTable: React.FC<ManagerPerformanceTableProps> = ({
  managers,
  title = 'Manager Performance',
  onManagerClick,
  className,
  isLoading = false,
}) => {
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAndFilteredManagers = useMemo(() => {
    let result = [...managers];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((m) => m.managerName.toLowerCase().includes(query));
    }

    // Sort
    result.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'name':
          aVal = a.managerName.toLowerCase();
          bVal = b.managerName.toLowerCase();
          break;
        case 'score':
          aVal = a.teamAverageScore;
          bVal = b.teamAverageScore;
          break;
        case 'teamSize':
          aVal = a.teamSize;
          bVal = b.teamSize;
          break;
        default:
          aVal = a.teamAverageScore;
          bVal = b.teamAverageScore;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [managers, sortField, sortDirection, searchQuery]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-stone-300" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-blue-500" />
    ) : (
      <ChevronDown className="w-4 h-4 text-blue-500" />
    );
  };

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-40 mb-6" />
          <div className="h-10 bg-stone-200 rounded w-full mb-4" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 py-4 border-b border-stone-100">
              <div className="w-10 h-10 bg-stone-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-stone-200 rounded w-32 mb-2" />
                <div className="h-3 bg-stone-200 rounded w-24" />
              </div>
              <div className="h-6 bg-stone-200 rounded w-16" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (!managers || managers.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div className="text-center py-8 text-stone-400">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No managers found</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
        <span className="text-sm text-stone-500">{managers.length} managers</span>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          placeholder="Search managers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200">
              <th
                className="text-left py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Manager
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="text-center py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700 w-28"
                onClick={() => handleSort('teamSize')}
              >
                <div className="flex items-center justify-center gap-1">
                  Team Size
                  <SortIcon field="teamSize" />
                </div>
              </th>
              <th
                className="text-center py-3 px-2 text-sm font-medium text-stone-500 cursor-pointer hover:text-stone-700 w-28"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center justify-center gap-1">
                  Team Score
                  <SortIcon field="score" />
                </div>
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-24">
                Trend
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-28">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredManagers.map((manager) => {
              const category = getScoreCategory(manager.teamAverageScore);

              return (
                <tr
                  key={manager.managerId}
                  className={clsx(
                    'border-b border-stone-100 last:border-b-0',
                    onManagerClick && 'hover:bg-stone-50 cursor-pointer'
                  )}
                  onClick={() => onManagerClick?.(manager.managerId)}
                >
                  {/* Manager */}
                  <td className="py-4 px-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-stone-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-stone-800 truncate">
                          {manager.managerName}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Team Size */}
                  <td className="py-4 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Users className="w-4 h-4 text-stone-400" />
                      <span className="text-sm font-medium text-stone-700">
                        {manager.teamSize}
                      </span>
                    </div>
                  </td>

                  {/* Team Score */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className="text-lg font-bold"
                      style={{ color: getScoreColor(manager.teamAverageScore) }}
                    >
                      {Math.round(manager.teamAverageScore)}
                    </span>
                  </td>

                  {/* Trend */}
                  <td className="py-4 px-2 text-center">
                    {manager.trend === 'up' && (
                      <div className="flex items-center justify-center gap-1 text-green-600">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs">Up</span>
                      </div>
                    )}
                    {manager.trend === 'down' && (
                      <div className="flex items-center justify-center gap-1 text-red-600">
                        <TrendingDown className="w-4 h-4" />
                        <span className="text-xs">Down</span>
                      </div>
                    )}
                    {manager.trend === 'stable' && (
                      <div className="flex items-center justify-center gap-1 text-stone-400">
                        <Minus className="w-4 h-4" />
                        <span className="text-xs">Stable</span>
                      </div>
                    )}
                  </td>

                  {/* Status */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className={clsx(
                        'inline-block px-2 py-1 rounded text-xs font-medium',
                        category === 'excellent' && 'bg-green-100 text-green-700',
                        category === 'good' && 'bg-blue-100 text-blue-700',
                        category === 'needsImprovement' && 'bg-amber-100 text-amber-700',
                        category === 'poor' && 'bg-red-100 text-red-700'
                      )}
                    >
                      {CATEGORY_LABELS[category]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedAndFilteredManagers.length === 0 && searchQuery && (
        <div className="text-center py-8 text-stone-400">
          <p>No managers match "{searchQuery}"</p>
        </div>
      )}
    </Card>
  );
};

export default ManagerPerformanceTable;
